import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { BRAND, DEMO_EMAIL, EASE } from './brand';

const SIZES = ['Under 25', '25\u201350', '50\u2013100', '100\u2013200', '200+'];

const FIELD =
  'w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20';

export default function DemoModal({ open, onClose }) {
  const reduce = useReducedMotion();
  const firstFieldRef = useRef(null);
  const [form, setForm] = useState({ club: '', name: '', size: SIZES[1], notes: '' });

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    const focusTimer = window.setTimeout(() => firstFieldRef.current?.focus(), 120);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(focusTimer);
    };
  }, [open, onClose]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = `Demo request \u2014 ${form.club || 'Rowing club'}`;
    const body = [
      `Hi ${BRAND} team,`,
      '',
      `We'd like a demo${form.club ? ` for ${form.club}` : ''}.`,
      '',
      `Club: ${form.club || '(not given)'}`,
      `Contact: ${form.name || '(not given)'}`,
      `Athletes: ${form.size}`,
      '',
      form.notes ? `What we're hoping to solve:\n${form.notes}` : "What we're hoping to solve:\n",
      '',
      'Thanks!',
    ].join('\n');

    window.location.href = `mailto:${DEMO_EMAIL}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6">
          <motion.div
            className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="demo-modal-title"
            initial={reduce ? false : { opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? undefined : { opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="relative w-full max-w-lg overflow-hidden rounded-t-3xl bg-white shadow-2xl safe-area-bottom sm:rounded-2xl"
          >
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
              <div>
                <h2 id="demo-modal-title" className="text-lg font-bold text-slate-900">
                  Request a demo
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  A couple of details so the walkthrough is about your program, not a generic tour.
                </p>
              </div>
              <button
                onClick={onClose}
                aria-label="Close"
                className="-mr-2 -mt-1 rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
              <div>
                <label htmlFor="demo-club" className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Club name
                </label>
                <input
                  id="demo-club"
                  ref={firstFieldRef}
                  value={form.club}
                  onChange={set('club')}
                  placeholder="Pennsylvania Athletic Club"
                  className={FIELD}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="demo-name" className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Your name
                  </label>
                  <input
                    id="demo-name"
                    value={form.name}
                    onChange={set('name')}
                    placeholder="Head coach, admin…"
                    className={FIELD}
                  />
                </div>
                <div>
                  <label htmlFor="demo-size" className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Athletes
                  </label>
                  <select id="demo-size" value={form.size} onChange={set('size')} className={FIELD}>
                    {SIZES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="demo-notes" className="mb-1.5 block text-xs font-semibold text-slate-700">
                  What are you trying to fix? <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <textarea
                  id="demo-notes"
                  value={form.notes}
                  onChange={set('notes')}
                  rows={3}
                  placeholder="Lineups live in a group chat and meters live in a notebook…"
                  className={`${FIELD} resize-none`}
                />
              </div>

              <button
                type="submit"
                className="w-full rounded-xl bg-slate-900 px-6 py-3.5 text-base font-semibold text-white transition-transform hover:scale-[1.01] active:scale-95"
              >
                Open a prefilled email
              </button>
              <p className="text-center text-xs text-slate-400">
                This opens your mail app with the message ready &mdash; nothing is sent until you hit
                send.
              </p>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
